/*
    BankTransferInstructions.jsx
    Displays Stripe bank transfer (jp_bank_transfer) details after PaymentIntent creation.
    No Stripe.js dependency - purely presentational.
*/

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FaUniversity, FaCopy, FaCheck, FaExternalLinkAlt } from "react-icons/fa";

export default function BankTransferInstructions({ data, amount, onClose }) {
    const { t } = useTranslation();
    const [copiedField, setCopiedField] = useState(null);

    const copyToClipboard = (text, fieldName) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        });
    };

    const bankDetails = data.bankDetails;
    const hostedUrl = data.hostedInstructionsUrl;

    const detailRows = bankDetails ? [
        { label: t('payment.bankTransfer.bankName'), value: bankDetails.bankName },
        { label: t('payment.bankTransfer.branchName'), value: bankDetails.branchName },
        { label: t('payment.bankTransfer.accountType'), value: bankDetails.accountType === 'futsu' ? t('payment.bankTransfer.futsu') : t('payment.bankTransfer.touza') },
        { label: t('payment.bankTransfer.accountNumber'), value: bankDetails.accountNumber },
        { label: t('payment.bankTransfer.accountHolder'), value: bankDetails.accountHolderName },
    ] : [];

    return (
        <motion.div
            className="bank-transfer-instructions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
        >
            <div className="bank-transfer-header">
                <FaUniversity />
                <h4>{t('payment.bankTransfer.title')}</h4>
            </div>

            <div className="bank-transfer-amount">
                <span className="bank-transfer-amount-label">{t('payment.bankTransfer.amountLabel')}</span>
                <span className="bank-transfer-amount-value">{amount.toLocaleString()}</span>
            </div>

            <div className="bank-transfer-notice">
                <p>{t('payment.bankTransfer.notice')}</p>
            </div>

            {bankDetails ? (
                <div className="bank-details-card">
                    {detailRows.map((row) => (
                        <div className="bank-detail-row" key={row.label}>
                            <span className="bank-detail-label">{row.label}</span>
                            <span className="bank-detail-value">
                                {row.value}
                                <button
                                    className="bank-detail-copy"
                                    onClick={() => copyToClipboard(row.value, row.label)}
                                    title="Copy"
                                >
                                    {copiedField === row.label ? <FaCheck /> : <FaCopy />}
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bank-transfer-notice">
                    <p>{t('payment.bankTransfer.fallbackNotice')}</p>
                </div>
            )}

            {hostedUrl && (
                <a
                    href={hostedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bank-transfer-hosted-link"
                >
                    {t('payment.bankTransfer.viewFullInstructions')} <FaExternalLinkAlt />
                </a>
            )}

            <div className="bank-transfer-footer">
                <p>{t('payment.bankTransfer.confirmationNote')}</p>
            </div>

            <button className="bank-transfer-close-btn" onClick={onClose}>
                {t('payment.bankTransfer.closeButton')}
            </button>
        </motion.div>
    );
}
